/**
 * Generate avatar initials from a full name string: the FIRST + LAST word's initial
 * (the conventional avatar scheme — Slack/Google/GitHub style). A single-word name
 * yields one initial. Returns '' for empty/non-string input (callers supply their own
 * fallback, e.g. `getInitials(name) || '?'` or a name/email chain).
 *
 * Note: no full-name → initials rule is universally correct — first+last mishandles
 * suffixes ("John Smith Jr." → "JJ"), just as first-N mishandles middle names. This is
 * the conventional default, not a canonical name parse.
 * @param name - Full name (e.g., "John Doe")
 * @returns Uppercase initials (e.g., "JD")
 * @example
 * getInitials("John Doe")         // "JD"
 * getInitials("Mary Jane Watson") // "MW"  (first + last, not "MJ")
 * getInitials("john")             // "J"
 */
export function getInitials(name: string): string {
  if (!name || typeof name !== 'string') {
    return '';
  }

  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Generate initials with fallback to email
 * Useful for user profiles where you have separate first/last name fields
 * @param firstName - User's first name
 * @param lastName - User's last name
 * @param email - User's email (fallback)
 * @returns Uppercase initials
 * @example
 * getInitialsWithFallback("John", "Doe") // "JD"
 * getInitialsWithFallback("John") // "J"
 * getInitialsWithFallback(undefined, undefined, "john@example.com") // "J"
 */
export function getInitialsWithFallback(
  firstName?: string,
  lastName?: string,
  email?: string
): string {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) {
    return firstName[0].toUpperCase();
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return 'U'; // Default fallback for "User"
}
